/**
 * CORE-1127 — classify a WatchChannel failure so the «Обсуждение» block renders
 * the RIGHT branch instead of collapsing every refusal into one connection
 * error with a useless «Повторить».
 *
 * The ChatApi SignalR hub (Agape.ChatApi/Hubs/ChatHub.cs · WatchChannel) answers
 * with two distinct, DETERMINISTIC refusals — no retry can change either:
 *
 *   channel == null                → HubException("channel not found")
 *   !CanReadAsync(channel, UserId) → HubException("forbidden")
 *
 * SignalR delivers a HubException's message to the JS client verbatim, so these
 * strings arrive unchanged in `err.message`. Everything else (a hung/timed-out
 * socket, a dropped connection, RU-edge silence) is a genuine transient failure
 * and stays retriable.
 *
 * We parse the message text rather than a machine code because the hub carries
 * no code channel — a HubException is only a string — and these strings are
 * already stable, code-like tokens. The coupling is guarded by
 * chatChannelError-CORE1127.test.ts, which pins the hub's literal wording so a
 * server-side rename fails a test here instead of silently folding the gate back
 * into «Проверьте соединение.».
 */

/**
 * Which UI branch a channel-access failure maps to.
 *
 * `unauthorized` (CORE-1127 att3) — the access token was rejected. It arrives
 * two ways: an HTTP 401 on SignalR negotiate (the token is stale/absent, so the
 * socket never opens) or a HubException("unauthenticated") from ChatHub.UserId
 * (a token with no id). Both are authorization failures, NOT connection blips,
 * and the honest UI is a re-auth prompt — never «Проверьте соединение.».
 */
export type ChannelAccessOutcome = 'forbidden' | 'notFound' | 'transient' | 'unauthorized';

function errorMessage(err: unknown): string {
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const m = (err as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  return '';
}

/**
 * CORE-1127 att3 — the HTTP status behind a transport failure, if any.
 *
 * A negotiate refusal reaches us wrapped by SignalR as a
 * FailedToNegotiateWithServerError whose message embeds the underlying
 * `HttpError` text — "…: Status code '401'". Some paths surface the bare
 * `HttpError`, which carries a numeric `statusCode`. Read both, preferring the
 * structured field.
 */
function httpStatus(err: unknown): number | null {
  if (err && typeof err === 'object') {
    const code = (err as { statusCode?: unknown }).statusCode;
    if (typeof code === 'number' && code >= 100 && code < 600) return code;
  }
  const m = /status code '?(\d{3})'?/i.exec(errorMessage(err));
  return m ? Number(m[1]) : null;
}

export function classifyChannelError(err: unknown): ChannelAccessOutcome {
  // CORE-1127 att3 — the negotiate refusal comes as an HTTP status, before any
  // HubException. A rejected token (401) or a denied connection (403) is
  // deterministic: no «Повторить» over the same token/path changes it.
  const status = httpStatus(err);
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  const msg = errorMessage(err).toLowerCase();
  // «forbidden»: the authed user isn't a member of the circle — offer the join
  // gate, not a retry.
  if (/\bforbidden\b/.test(msg)) return 'forbidden';
  // «channel not found»: match the WatchChannel wording specifically. A bare
  // "not found" also rides the Edit/Delete/React HubExceptions ("not found or
  // not editable", "message not found"), which are never seen during connect and
  // must not be read as a missing channel.
  if (msg.includes('channel not found')) return 'notFound';
  // «unauthenticated»: ChatHub.UserId's HubException for a token with no id —
  // the transport twin of an HTTP 401. isHubRefusal already treats it as
  // deterministic; the classifier must send it to the same auth branch instead
  // of «Проверьте соединение.» (found-not-fixed in attempt 2).
  if (/\bunauthenticated\b/.test(msg)) return 'unauthorized';
  return 'transient';
}
